import React, { useState } from "react";
import { ArrowRight, ArrowUpRight, BookOpen, Check, ChevronDown, Copy, FileText, Plug, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { copyText, MCP_SERVER_URL } from "@/lib/mcp";
import { capture } from "@/lib/posthog";
import { onboardingTestIds as tid } from "@/testids/onboardingTestIds";
import { connectorLogoUrl } from "@/lib/connector-logo";
import { AI_TOOLS, aiToolInstall, aiToolLabel, mcpLinkLabel, type AiToolId } from "./ai-tools";
import { DOCS_LINKS } from "./docs-links";
import { FastnMark, Overlay, PopTitle } from "./ob3";


/** The tool's logo, from its domain. Hidden when it does not load, so the label stands alone. */
function AiToolLogo({ id }: { id: AiToolId }): React.ReactElement | null {
  const [failed, setFailed] = useState(false);
  const src = connectorLogoUrl(undefined, AI_TOOLS.find((t) => t.id === id)?.domain);
  if (!src || failed) return null;
  return <img className="tsel-logo" src={src} alt="" aria-hidden="true" onError={() => setFailed(true)} />;
}

/** The tool dropdown: the design's `details.tsel`, closed again on pick. */
export function AiToolSelect({
  value,
  fromAnswer,
  onChange,
}: {
  value: AiToolId;
  /** The value is still the one their signup answer picked. */
  fromAnswer: boolean;
  onChange: (tool: AiToolId) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <details className="cpop-other tsel" open={open} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary className="tsel-btn" data-testid={tid.aiToolSelect}>
        <AiToolLogo id={value} />
        <span className="t13 b">{aiToolLabel(value)}</span>
        <span className="t12 sub flex-1!">{fromAnswer ? "from your setup answer" : ""}</span>
        <span className="chev flex! [color:var(--fgColor-subtle)]!"><ChevronDown className="i xs" aria-hidden="true" /></span>
      </summary>
      <div className="tsel-menu" role="listbox" aria-label="AI tool">
        {AI_TOOLS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="option"
            aria-selected={t.id === value}
            className="tsel-it w-full! text-left! cursor-pointer!"
            onClick={() => { onChange(t.id); setOpen(false); }}
            data-testid={tid.aiToolOption(t.id)}
          >
            <AiToolLogo id={t.id} />
            <span className="t13 flex-1!">{t.label}</span>
            {t.id === value && <Check className="i xs" aria-hidden="true" />}
          </button>
        ))}
        <div className="tsel-it cursor-default!">
          <span className="t13 flex-1!">Other <span className="t12 sub">MCP link</span></span>
          <button
            type="button"
            className="btn btn--secondary btn--sm cursor-pointer!"
            title={mcpLinkLabel()}
            onClick={async () => { setCopied(await copyText(MCP_SERVER_URL)); capture("mcp_url_copied", { target: "onboarding", method: "copy" }); }}
            data-testid={tid.aiToolCopyLink}
          >
            {copied ? <Check className="i xs" aria-hidden="true" /> : <Copy className="i xs" aria-hidden="true" />} {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      </div>
    </details>
  );
}

/** Opens the tool with fastn's MCP server, copying the link where it cannot be prefilled. */
export function openAiTool(tool: AiToolId): void {
  const { href, copiesLink } = aiToolInstall(tool);
  if (copiesLink) void copyText(MCP_SERVER_URL);
  window.open(href, "_blank", "noopener,noreferrer");
  capture("mcp_install_link_opened", { client: tool });
}

/** The select plus its "Add to <tool>" button, as one row. */
export function AiToolAddRow({
  tool,
  fromAnswer,
  onTool,
  onAdd,
}: {
  tool: AiToolId;
  fromAnswer: boolean;
  onTool: (t: AiToolId) => void;
  onAdd: () => void;
}): React.ReactElement {
  return (
    <div className="flex! items-start! gap-[8px]!">
      <div className="flex-1! min-w-0!">
        <AiToolSelect value={tool} fromAnswer={fromAnswer} onChange={onTool} />
      </div>
      <button
        type="button"
        className="btn btn--primary btn--sm cursor-pointer!"
        onClick={onAdd}
        data-testid={tid.aiToolAdd}
      >
        Add to {aiToolLabel(tool)} <ArrowUpRight className="i xs" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * "Finish setup in Claude / in your agent": what to do in the tool that just
 * opened. Done takes them to fastn, where Your first integration shows progress.
 */
export function FinishSetupCard({
  tool,
  onClose,
  onDone,
}: {
  tool: AiToolId;
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const isClaude = tool === "claude";
  const where = isClaude ? "Claude" : "your agent";
  return (
    <Overlay label={`Finish setup in ${where}`} onClose={onClose} raised testId={tid.finishSetupCard}>
      <PopTitle>Finish setup in {where}</PopTitle>
      <div className="t13 mut [margin-top:-6px]!">You finish setup in {where}. It opened in a new tab.</div>
      <div className="flex! flex-col! gap-[12px]!">
        {[
          [`Approve fastn in ${where}`, "Click Allow when it asks."],
          isClaude
            ? ["Claude guides you through the 4 steps", "Answer its questions as it goes."]
            : ["Type “Set up fastn for me” in the chat", "Answer its questions as it goes."],
          ["Come back here when you’re done", "Your progress shows in Your first integration."],
        ].map(([title, note], i) => (
          <div key={title} className="flex! gap-[10px]! items-start!">
            <span className="num w-[20px]! h-[20px]! [flex:0_0_20px]! ob3-fs10">{i + 1}</span>
            <span>
              <span className="b t13 block!">{title}</span>
              <span className="t12 mut">{note}</span>
            </span>
          </div>
        ))}
      </div>
      <div className="t12 flex! items-center! gap-[14px]!">
        <span className="sub">Docs</span>
        <a className="lnk inline-flex! items-center! gap-[4px]!" href={tool === "claude" ? DOCS_LINKS.mcpClaude : tool === "chatgpt" ? DOCS_LINKS.mcpChatgpt : DOCS_LINKS.mcp} target="_blank" rel="noreferrer" data-testid={tid.finishSetupDocs}>
          <BookOpen className="i xs" aria-hidden="true" /> {tool === "claude" ? "Add fastn to Claude" : tool === "chatgpt" ? "Add fastn to ChatGPT" : "fastn’s MCP server"}
        </a>
        <a className="lnk inline-flex! items-center! gap-[4px]!" href={DOCS_LINKS.quickstart} target="_blank" rel="noreferrer" data-testid={tid.finishSetupGuide}>
          <FileText className="i xs" aria-hidden="true" /> Quickstart
        </a>
      </div>
      <div className="flex! items-center! gap-[8px]!">
        <button type="button" className="btn btn--ghost btn--sm cursor-pointer!" onClick={() => openAiTool(tool)} data-testid={tid.finishSetupReopen}>
          Open {isClaude ? "Claude" : "your agent"} again <ArrowUpRight className="i xs" aria-hidden="true" />
        </button>
        <span className="flex-1!" />
        <button type="button" className="btn btn--primary btn--lg cursor-pointer!" onClick={onDone} data-testid={tid.finishSetupDone}>
          Done, go to fastn <ArrowRight className="i xs" aria-hidden="true" />
        </button>
      </div>
    </Overlay>
  );
}

export type AiPopoverMode =
  /** How it works: the two ways to set up. */
  | "choose"
  /** Every later screen: carry on in the AI tool. */
  | "continue";

/**
 * The header's "Connect AI tools" button and its pop-up. Picking Add opens the
 * tool and swaps this pop-up for the Finish setup card, as the design does.
 */
export function AiSetupPopover({
  mode,
  tool,
  fromAnswer,
  onTool,
  open,
  onOpenChange,
  onStartHere,
  onDone,
  trigger,
  triggerClassName = "btn btn--secondary btn--sm",
  title = "Continue in your AI tool",
  subtitle = "0 AI credits. Your AI tool guides you through the rest, and progress shows here.",
  testId,
  hideTriggerWhenClosed = false,
}: {
  mode: AiPopoverMode;
  tool: AiToolId;
  fromAnswer: boolean;
  onTool: (t: AiToolId) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Here in fastn · Start here" (choose mode). */
  onStartHere?: () => void;
  /** "Done, go to fastn" on the Finish setup card. */
  onDone: () => void;
  /** The button that opens it. The header's "Connect AI tools" unless given. */
  trigger?: React.ReactNode;
  triggerClassName?: string;
  /** Continue mode's heading and line; the build screens word them for what gets built. */
  title?: string;
  subtitle?: string;
  testId?: string;
  /** How it works shows no button until Get started opens the pop-up, as drawn. */
  hideTriggerWhenClosed?: boolean;
}): React.ReactElement {
  const [finishing, setFinishing] = useState(false);
  const add = () => {
    openAiTool(tool);
    onOpenChange(false);
    setFinishing(true);
  };
  return (
    <>
      <details className="cpop" open={open} onToggle={(e) => onOpenChange((e.currentTarget as HTMLDetailsElement).open)}>
        <summary className={cn(triggerClassName, hideTriggerWhenClosed && !open && "hidden!")} data-testid={testId ?? tid.connectAiTools}>
          {trigger ?? <><Plug className="i xs" aria-hidden="true" /> <span className="max-md:sr-only!">Connect AI tools</span></>}
          <span className="pop-bg" aria-hidden="true" />
          <span className="pop-x" role="button" aria-label="Close"><X className="i sm" aria-hidden="true" /></span>
        </summary>
        <div className="cpop-card" role="dialog" aria-label="How do you want to set up?" data-testid={tid.aiPopover}>
          {mode === "choose" ? (
            <>
              <PopTitle>How do you want to set up?</PopTitle>
              <div className="t13 mut [margin-top:-6px]!">Same 4 steps either way, and both earn your 50 setup AI credits. Switch anytime.</div>
              <div className="cpop-opt cursor-default! flex-col! items-stretch! gap-[10px]!">
                <div className="flex! items-center! gap-[12px]!">
                  <span className="logo w-[36px]! h-[36px]!"><Sparkles className="i sm" aria-hidden="true" /></span>
                  <span className="flex-1! min-w-0!">
                    <span className="b flex! items-center! gap-[8px]!">In your AI tool <span className="tag">Recommended</span></span>
                    <span className="t12 mut">0 AI credits. It guides you through each step.</span>
                  </span>
                </div>
                <AiToolAddRow tool={tool} fromAnswer={fromAnswer} onTool={onTool} onAdd={add} />
              </div>
              <div className="cpop-opt cursor-default! flex-col! items-stretch! gap-[10px]!">
                <div className="flex! items-center! gap-[12px]!">
                  <span className="logo w-[36px]! h-[36px]!"><FastnMark height={18} /></span>
                  <span className="flex-1! min-w-0!">
                    <span className="b flex! items-center! gap-[8px]! whitespace-nowrap!">Here in fastn</span>
                    <span className="t12 mut">Guided screens, one step at a time.</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm cursor-pointer!"
                    onClick={() => { onOpenChange(false); onStartHere?.(); }}
                    data-testid={tid.startHere}
                  >
                    Start here <ArrowRight className="i xs" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <PopTitle>{title}</PopTitle>
              <div className="t13 mut [margin-top:-6px]!">{subtitle}</div>
              <div className="cpop-opt cursor-default! flex-col! items-stretch! gap-[10px]!">
                <AiToolAddRow tool={tool} fromAnswer={fromAnswer} onTool={onTool} onAdd={add} />
              </div>
            </>
          )}
        </div>
      </details>
      {finishing && <FinishSetupCard tool={tool} onClose={() => setFinishing(false)} onDone={onDone} />}
    </>
  );
}
